import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Church, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { serviceSchedule, siteMeta } from "../../siteData";
import { createPageUrl } from "../../utils";
import { useLanguage } from "../../i18n";

export default function HeroSection() {
  const { t } = useLanguage();
  const localizedServiceSchedule = t("home.serviceSchedule", serviceSchedule);
  const currentService = localizedServiceSchedule[0] || serviceSchedule[0];

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 opacity-20 ethiopian-cross" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-28 lg:py-32">
        <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 mb-6">
              <Church className="w-4 h-4 text-[#C9A227]" />
              <span className="text-[#f3dcc4] text-sm font-medium font-sans-clean">
                {t("home.heroBadge")}
              </span>
            </div>

            <h1 className="font-serif-elegant text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t("home.heroTitle")}
            </h1>

            <p className="text-lg sm:text-xl text-white/80 leading-relaxed font-sans-clean mb-8 max-w-2xl">
              {t("home.heroBody")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={createPageUrl("Contact")}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-[#C9A227] text-[#5b2229] font-sans-clean font-semibold text-sm sm:text-base hover:bg-[#d7b751] transition-colors"
              >
                {t("home.heroPrimary")}
                <ArrowRight className="w-4 h-4" />
              </Link>

              <Link
                to={createPageUrl("About")}
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg border border-white/60 text-white font-sans-clean text-sm sm:text-base hover:bg-white/10 transition-colors"
              >
                {t("home.heroSecondary")}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="panel-surface rounded-[2rem] border border-white/20 p-6 sm:p-8 shadow-2xl"
          >
            <p className="text-sm uppercase tracking-[0.22em] text-[#722F37]/70 font-semibold mb-4">
              {t("home.visitThisWeek")}
            </p>
            <h2 className="font-serif-elegant text-3xl text-[#722F37] font-bold mb-3">
              {currentService.title}
            </h2>
            <p className="text-gray-700 text-sm mb-5">{currentService.detail}</p>

            <div className="space-y-4 text-sm text-gray-700">
              <div className="rounded-2xl bg-white p-4 border border-[#C9A227]/20">
                <p className="text-xs uppercase tracking-[0.2em] text-[#722F37]/60 mb-1">
                  {t("home.time")}
                </p>
                <p className="font-semibold text-[#722F37]">{currentService.time}</p>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-[#C9A227]/20">
                <p className="text-xs uppercase tracking-[0.2em] text-[#722F37]/60 mb-1">
                  {t("home.location")}
                </p>
                <p className="font-semibold text-[#722F37]">{siteMeta.addressLine1}</p>
                <p>{siteMeta.addressLine2}</p>
                <p className="mt-2 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#C9A227] mt-0.5 flex-shrink-0" />
                  <span>{t("layout.meetingLocation", siteMeta.meetingLocation)}</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
